import { useEffect, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { OnePersonListItem } from '@factorialco/f0-react/dist/experimental'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  AbsencesHomeWidgetDocument,
  HomePageDocument,
  TeamStatusActivityDocument,
  TimeInsightsEntitiesClockInStatusStatusEnum,
} from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { AttendanceFeaturesProvider } from 'modules/attendance/lib/attendanceFeaturesContext'

import Item from './Item'
import SendEmailReminder from './sendEmailReminder'

type Data = {
  status: TimeInsightsEntitiesClockInStatusStatusEnum
}

export const TeamStatusActivity: ActivityComponent<Data> = ({ status }) => {
  const [emailReminder, setEmailReminder] = useState<{
    employeeId: string
    employeeFullName: string
  } | null>(null)

  const { open, closeActivity } = useActivity()
  const { company } = useCurrentSession()
  const { data: homePageData } = useQuery(HomePageDocument, {
    fetchPolicy: 'cache-only',
    variables: {
      companyId: company.id,
    },
  })

  const timeoffModuleEnabled =
    homePageData?.companies.moduleAvailabilitiesConnection.nodes?.some(
      (module) => module?.id === AvailableModules.timeoff && module?.available
    ) ?? false

  const { data: absencesData } = useQuery(AbsencesHomeWidgetDocument, {
    fetchPolicy: 'cache-only',
    variables: {
      today: dateToString(moment(), 'backend'),
    },
  })

  const statuses = absencesData?.timeInsights.clockInStatusesConnection.nodes || []
  const groups = groupBy(statuses, 'status')

  const counters = useMemo(
    () => ({
      outOfOffice: groups.should_not_be_working?.length ?? 0,
      missingClockIn: groups.missing_clock_in?.length ?? 0,
      clockedIn: groups.clocked_in?.length ?? 0,
      inABreak: groups.in_a_break?.length ?? 0,
    }),
    [groups]
  )

  const handleOpenEmailReminder = ({
    employeeId,
    employeeFullName,
  }: {
    employeeId: string
    employeeFullName: string
  }) => {
    setEmailReminder({ employeeId, employeeFullName })
  }

  const handleCloseEmailReminder = () => {
    setEmailReminder(null)
  }

  const tabs = useMemo<{ id: TimeInsightsEntitiesClockInStatusStatusEnum; label: string }[]>(
    () =>
      [
        counters.missingClockIn > 0 && {
          id: 'missing_clock_in' as const,
          label: i18n.t('dashboard.who_is_in.missing_clock_in'),
        },
        counters.clockedIn > 0 && {
          id: 'clocked_in' as const,
          label: i18n.t('dashboard.who_is_in.clocked_in'),
        },
        counters.inABreak > 0 && {
          id: 'in_a_break' as const,
          label: i18n.t('dashboard.who_is_in.in_a_break'),
        },
        counters.outOfOffice > 0 && {
          id: 'should_not_be_working' as const,
          label: i18n.t('employees.away'),
        },
      ].filter(Boolean),
    [counters]
  )

  const [activeTabId, setActiveTabId] = useState<string>(status)

  useEffect(() => {
    setActiveTabId(status)
  }, [status])

  const today = dateToString(moment(), 'backend')

  const { data, loading } = useQuery(TeamStatusActivityDocument, {
    variables: {
      // @ts-expect-error - activeTabId is always a valid enum value from tabs
      status: activeTabId,
      today,
      skipLeaves: !timeoffModuleEnabled,
    },
    errorPolicy: 'all',
  })

  const items = sortBy(data?.timeInsights.clockInStatusesConnection.nodes ?? [], [
    (item) => item.employee?.preferredName ?? '',
    (item) => item.employee?.firstName ?? '',
    (item) => item.employee?.lastName ?? '',
  ])

  const showLoading = loading && !items.length

  return (
    <F0Dialog
      isOpen={open}
      onClose={closeActivity}
      title={i18n.t('dashboard.who_is_in.title')}
      tabs={emailReminder ? undefined : tabs}
      activeTabId={activeTabId}
      setActiveTabId={setActiveTabId}
    >
      {emailReminder ? (
        <AttendanceFeaturesProvider>
          <SendEmailReminder
            employeeId={emailReminder.employeeId}
            employeeFullName={emailReminder.employeeFullName}
            onClose={handleCloseEmailReminder}
          />
        </AttendanceFeaturesProvider>
      ) : (
        <Box flexDirection="column" gap="s2" paddingX="s8" paddingY="s16">
          {showLoading
            ? new Array(20).fill(null).map((_, i) => <OnePersonListItem.Skeleton key={i} />)
            : items.map((item, i) => (
                <Item key={i} data={item} onEmailReminder={handleOpenEmailReminder} />
              ))}
        </Box>
      )}
    </F0Dialog>
  )
}
